import { useState, useImperativeHandle, forwardRef, FormEvent, ChangeEvent } from "react";
import { TbBuildingStore } from "react-icons/tb";
import api from "../../../../Services/api";
import { useToast } from "../../../../Hooks/toast";
import { ValutDialog } from "../../../../Components/Molecules/ValutDialog";
import { ValutField, ValutInput } from "../../../../Components/Atoms/ValutInput";

export type OrginizerFormRef = {
  openOrganizer: (props: { name: string; subAccountId: string; isActive: boolean }) => void;
};

export const OrganizerForm = forwardRef<OrginizerFormRef, { onSucess: VoidFunction }>(
  ({ onSucess = () => {} }, ref) => {
    const [open, setOpen] = useState(false);

    const [subAccount, setSubAccount] = useState({
      subAccountId: "",
      name: "",
      isActive: true,
    });

    const { addToast } = useToast();

    const [isLoading, setIsLoading] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        openOrganizer({ name = "", subAccountId = "", isActive = true }) {
          setSubAccount({
            name,
            subAccountId,
            isActive,
          });
          setOpen(true);
        },
      }),
      []
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.currentTarget.name;
      const value =
        e.currentTarget.type === "checkbox" ? e.currentTarget.checked : e.currentTarget.value;

      setSubAccount((oldSubAccount) => ({ ...oldSubAccount, [name]: value }));
    };

    const handleClose = () => setOpen(false);

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();

      try {
        setIsLoading(true);

        if (subAccount.subAccountId) {
          await api.put(`/subaccount/${subAccount.subAccountId}`, {
            name: subAccount.name,
            isActive: subAccount.isActive,
          });
        } else {
          await api.post("/subaccount", {
            name: subAccount.name,
          });
        }

        addToast({
          title: "Sucesso",
          description: "Ponto de venda salvo com sucesso",
          type: "success",
        });

        setOpen(false);
      } finally {
        setIsLoading(false);
        onSucess();
      }
    };

    const isEditing = Boolean(subAccount.subAccountId);

    return (
      <ValutDialog
        open={open}
        onClose={handleClose}
        title={isEditing ? "Editar ponto de venda" : "Adicionar ponto de venda"}
        titleId="organizer-form-title"
        icon={<TbBuildingStore className="h-4 w-4" />}
        maxWidth="sm"
        footer={
          <button
            form="frmSubAccount"
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={!subAccount.name.trim() || isLoading}
          >
            {isLoading ? "Salvando..." : "Salvar"}
          </button>
        }
      >
        <form id="frmSubAccount" onSubmit={handleSubmit} className="space-y-4">
          <ValutField label="Nome do ponto de venda" htmlFor="organizer-name">
            <ValutInput
              id="organizer-name"
              name="name"
              value={subAccount.name}
              onChange={handleChange}
              placeholder="Ex.: Caixa 01 - Loja Centro"
              autoFocus
            />
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Como este PV será identificado internamente.
            </p>
          </ValutField>

          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              name="isActive"
              checked={subAccount.isActive}
              onChange={handleChange}
              disabled={!isEditing}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#e7a73f] focus:ring-[#e7a73f]/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
            />
            <span className="text-sm text-brand-dark dark:text-gray-100">
              Ponto de venda ativo
              {!isEditing ? (
                <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-gray-400">
                  Novos pontos de venda são criados como ativos.
                </span>
              ) : null}
            </span>
          </label>
        </form>
      </ValutDialog>
    );
  }
);
